import type { CollectionOffers, Offer } from './offers'

export type OfferBookFilter = 'collection' | 'trait' | 'item' | 'mine'
export interface OfferLevel {
	key: string
	price: number
	scope: Offer['scope']
	label: string
	offers: Offer[]
	total: number
}

export function activeCollectionOffers(data: CollectionOffers | undefined, now: number): Offer[] {
	return [
		...new Map(
			[...(data?.collection_offers ?? []), ...(data?.trait_offers ?? []), ...(data?.offers ?? [])]
				.filter(
					(o) =>
						o.state === 'active' &&
						Number.isSafeInteger(o.price_sats) &&
						o.price_sats > 0 &&
						Date.parse(o.expires_at) > now
				)
				.map((o) => [o.id, o])
		).values()
	].sort((a, b) => b.price_sats - a.price_sats)
}

export function offerLevels(
	offers: Offer[],
	filter: OfferBookFilter,
	wallet: string | null,
	grouped: boolean
): OfferLevel[] {
	const levels = new Map<string, OfferLevel>()
	for (const offer of offers) {
		if (filter === 'mine' ? offer.buyer_address !== wallet : offer.scope !== filter) continue
		// Equal prices with different eligibility are separate markets.
		const key = grouped
			? JSON.stringify([
					offer.scope,
					offer.price_sats,
					offer.trait_type,
					offer.trait_value,
					offer.inscription_id
			  ])
			: offer.id
		let level = levels.get(key)
		if (!level) {
			level = {
				key,
				price: offer.price_sats,
				scope: offer.scope,
				label:
					offer.scope === 'trait'
						? `${offer.trait_type}: ${offer.trait_value}`
						: offer.scope === 'item'
						? 'Specific item'
						: 'Any item',
				offers: [],
				total: 0
			}
			levels.set(key, level)
		}
		level.offers.push(offer)
		level.total += offer.price_sats
	}
	return [...levels.values()].sort((a, b) => b.price - a.price)
}

/** Only seed receipts confirmed active by the API, never unsigned/pending bids. */
export function seedConfirmedCollectionOffer(
	current: CollectionOffers | undefined,
	offer: Offer,
	slug: string
): CollectionOffers | undefined {
	if (offer.state !== 'active') return current
	const base = current ?? {
		slug,
		offers: [],
		collection_offers: [],
		trait_offers: [],
		summary: {
			count: 0,
			item_count: 0,
			collection_count: 0,
			trait_count: 0,
			top_price_sats: 0,
			top_collection_sats: 0,
			total_sats: 0
		}
	}
	const result = {
		...base,
		offers: base.offers.filter((o) => o.id !== offer.id),
		collection_offers: base.collection_offers.filter((o) => o.id !== offer.id),
		trait_offers: base.trait_offers.filter((o) => o.id !== offer.id)
	}
	const key =
		offer.scope === 'collection'
			? 'collection_offers'
			: offer.scope === 'trait'
			? 'trait_offers'
			: 'offers'
	result[key] = [offer, ...result[key]]
	const active = activeCollectionOffers(result, Date.now())
	result.summary = {
		...base.summary,
		count: active.length,
		item_count: active.filter((o) => o.scope === 'item').length,
		collection_count: active.filter((o) => o.scope === 'collection').length,
		trait_count: active.filter((o) => o.scope === 'trait').length,
		top_price_sats: Math.max(0, ...active.map((o) => o.price_sats)),
		top_collection_sats: Math.max(
			0,
			...active.filter((o) => o.scope === 'collection').map((o) => o.price_sats)
		),
		total_sats: active.reduce((sum, o) => sum + o.price_sats, 0)
	}
	return result
}
